import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';
import { Node as INode, RenderNode as IRenderNode, Connection as IConnection, Project as IProject } from './peregrineFlowInterfaces';
import { trackEngine, trackNode, trackProject } from './analytics';
import { socket } from './socket';
import {
  Edge,
  EdgeChange,
  Node,
  NodeChange,
  OnNodesChange,
  OnEdgesChange,
  applyNodeChanges,
  applyEdgeChanges,
  OnConnect,
  Connection,
  addEdge,
} from 'reactflow';


export interface CustomReactFlowNode {
  id: string;
  type: string;
  targetPosition: string;
  position: { x: number, y: number };
  data: INode | IRenderNode
}

export type AppView = 'projectList' | 'editor';

export type RFState = {
    username: string;
    token?: string;
    connected: boolean;
    isGuest: boolean;
    isAuthenticated: boolean;
    showLoginDialog: boolean;
    showSaveAsDialog: boolean;
    showNodeProvider: boolean;
    showSignUpDialog: boolean;
    signUpDialogReason: 'runLimit' | 'premiumNode' | null;
    engineRunCount: number;
    hasSeenRunLimitDialog: boolean;
    currentView: AppView;
    selectedProject: IProject | null;
    nodes: Node<CustomReactFlowNode>[];
    edges: Edge[];
    appEdges: IConnection[];
    project: IProject | null;
    selectedNodes: CustomReactFlowNode[],
    isRunning: boolean;
    lockEngine: boolean;
    onConnect: OnConnect
    onNodesChange: OnNodesChange;
    onEdgesChange: OnEdgesChange;
    addNode: (node: Node<CustomReactFlowNode>) => void;
    removeNode: (node: Node<CustomReactFlowNode>) =>void;
    setIsRunning: (isRunning: boolean) => void;
    setLockEngine: (lockEngine: boolean) => void;
    setUsername: (username: string) => void;
    setToken: (token: string|undefined) => void;
    setConnected: (connected: boolean) => void;
    setIsGuest: (isGuest: boolean) => void;
    setIsAuthenticated: (isAuthenticated: boolean) => void;
    setShowLoginDialog: (show: boolean) => void;
    setShowSaveAsDialog: (show: boolean) => void;
    setShowNodeProvider: (show: boolean) => void;
    setShowSignUpDialog: (show: boolean, reason?: 'runLimit' | 'premiumNode') => void;
    incrementEngineRunCount: () => void;
    resetEngineRunCount: () => void;
    setCurrentView: (view: AppView) => void;
    setSelectedProject: (project: IProject | null) => void;
    selectProject: (project: IProject) => void;
    openProjectList: () => void;
    setNodes: (nodes: Node<CustomReactFlowNode>[]) => void;
    setEdges: (edges: Edge[]) => void;
    setAppEdges: (appEdges: IConnection[]) => void;
    setSelectedNodes: (nodes: CustomReactFlowNode[]) => void;
    setProject: (project: IProject) => void;
    clearProject: () => void;
  };


const useStore = create<RFState>((set, get) => ({
    username: "",
    token: undefined,
    connected: false,
    isGuest: true,
    isAuthenticated: false,
    showLoginDialog: false,
    showSaveAsDialog: false,
    showNodeProvider: false,
    showSignUpDialog: false,
    signUpDialogReason: null,
    engineRunCount: 0,
    hasSeenRunLimitDialog: false,
    currentView: 'projectList' as AppView,
    selectedProject: null,
    nodes: [],
    edges: [],
    project: null,
    appEdges: [],
    selectedNodes: [],
    isRunning: false,
    lockEngine: false,
    potentialNodePosition: {x: 0, y: 0},
    onNodesChange: (changes: NodeChange[]) => {
      set({
        nodes: applyNodeChanges(changes, get().nodes),
      });
    },
    onEdgesChange: (changes: EdgeChange[]) => {
      const currentEdges = get().edges;
      const nodes = get().nodes;

      // Check for removed edges and emit disconnect events
      changes.forEach(change => {
        if (change.type === 'remove') {
          const removedEdge = currentEdges.find(e => e.id === change.id);
          if (removedEdge) {
            const sourceNode = nodes.find(n => n.id === removedEdge.source);
            const targetNode = nodes.find(n => n.id === removedEdge.target);

            if (sourceNode && targetNode && removedEdge.sourceHandle && removedEdge.targetHandle) {
              socket.emit('port_disconnect', {
                event_type: 'disconnect',
                provider_node: sourceNode.data as unknown as INode,
                provider_port: removedEdge.sourceHandle,
                consumer_node: targetNode.data as unknown as INode,
                consumer_port: removedEdge.targetHandle,
              });
            }
          }
        }
      });

      set({
        edges: applyEdgeChanges(changes, currentEdges),
      });
    },
    onConnect: (connection: Connection) => {
      set({
        edges: addEdge(connection, get().edges),
      });

      // Emit port_connect event to backend for auto-detection support
      const nodes = get().nodes;
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (sourceNode && targetNode && connection.sourceHandle && connection.targetHandle) {
        socket.emit('port_connect', {
          event_type: 'connect',
          provider_node: sourceNode.data as unknown as INode,
          provider_port: connection.sourceHandle,
          consumer_node: targetNode.data as unknown as INode,
          consumer_port: connection.targetHandle,
        });
      }
    },
    addNode: (node: Node<CustomReactFlowNode>) => {
      trackNode('add', node.type);
      set({
        nodes: [...get().nodes, node]
      })
    },
    removeNode: (node: Node<CustomReactFlowNode>) => {
      trackNode('delete', node.type);
      set({
        nodes: get().nodes.filter((n) => n.id !== node.id)
      })
    },
    setIsRunning: (isRunning: boolean) => {
      // Track engine state changes
      const wasRunning = get().isRunning;
      if (isRunning !== wasRunning) {
        trackEngine(isRunning ? 'start' : 'stop');
      }
      set({
        isRunning: isRunning
      })
    },
    setLockEngine: (lockEngine: boolean) => {
      set({
        lockEngine: lockEngine
      })
    },
    setUsername(username) {
      set({
        username: username
      })
    },
    setToken(token: string|undefined) {
      set({
        token: token
      })
    },
    setConnected(connected) {
      set({
        connected: connected
      })
    },
    setIsGuest(isGuest) {
      set({
        isGuest: isGuest
      })
    },
    setIsAuthenticated(isAuthenticated) {
      set({
        isAuthenticated: isAuthenticated
      })
    },
    setShowLoginDialog(show) {
      set({
        showLoginDialog: show
      })
    },
    setShowSaveAsDialog(show) {
      set({
        showSaveAsDialog: show
      })
    },
    setShowNodeProvider(show) {
      set({
        showNodeProvider: show
      })
    },
    setShowSignUpDialog(show, reason) {
      set({
        showSignUpDialog: show,
        signUpDialogReason: show ? (reason || null) : null
      })
    },
    incrementEngineRunCount() {
      set({
        engineRunCount: get().engineRunCount + 1
      })
    },
    resetEngineRunCount() {
      set({
        engineRunCount: 0,
        hasSeenRunLimitDialog: true
      })
    },
    setCurrentView(view) {
      set({
        currentView: view
      })
    },
    setSelectedProject(project) {
      set({
        selectedProject: project
      })
    },
    selectProject(project) {
      trackProject('open', project.name);
      set({
        selectedProject: project,
        currentView: 'editor'
      })
    },
    openProjectList() {
      get().clearProject();
      set({
        selectedProject: null,
        currentView: 'projectList'
      })
    },
    setNodes: (nds: Node<CustomReactFlowNode>[]) => {
      set({
        nodes: nds
      })
    },
    setEdges: (eds: Edge[]) => {
      set({
        edges: eds
      })
    },
    setAppEdges: (eds: IConnection[]) => {
      set({
        appEdges: eds
      })
    },
    setSelectedNodes: (nds: CustomReactFlowNode[]) => {
      set({
        selectedNodes: nds
      })
    },
    setProject: (project: IProject) => {
      get().clearProject();
      set({
        project: project
      })
    },
    clearProject: () => {
      // Stop the engine on the backend before clearing the project
      socket.emit('engine', { event_type: 'stopping', connections: undefined });

      set({
        edges: [],
        nodes: [],
        selectedNodes: [],
        project: null
      })
    }
  }));

  export default useStore;

export const nodesToCustomReactFlowNode = (nodes: Array<INode>) => {
    const nodeList = nodes.map((node) => nodeToCustomReactFlowNode(node))
    return nodeList;
}

export const nodeToCustomReactFlowNode = (node: INode) => {
    const id = node.id === "" ? `${node.name}-${uuidv4()}` : node.id
    const node_tpl = Object.assign({}, node);
    node_tpl.id = id

    const n = {
        id: id,
        type: node.node_type,
        targetPosition: "",
        position: { x: 0, y: 0 },
        data: node_tpl
    } as unknown as Node<CustomReactFlowNode>;

    return n;
}
